import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDocs,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { updateDeCaucion, numeroDe, CONCILIACION_YA_CARGADA } from '../utils/caucionesConciliacion';
import { parsearArchivoCauciones, bytesAMatriz } from '../utils/caucionesImport';

const CAUCIONES = 'cauciones';
const PARCIALES = 'parciales';
const IMPORTADAS = 'caucionesImportadas';

// Firestore admite 500 escrituras por batch; se corta antes para dejar margen.
const BATCH_SIZE = 400;

export const CONCILIACION_ASIGNADA = 'Asignada';
export const CONCILIACION_IGNORADA = 'Ignorada';

// ---------------------------------------------------------------------------
// Lectura del archivo
// ---------------------------------------------------------------------------

/**
 * Lee el archivo que eligió el trader y devuelve las partidas ya normalizadas.
 *
 * @param {File} file
 * @param {{ fechaRueda?: Date }} opciones
 */
export const leerArchivoCauciones = (file, { fechaRueda } = {}) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.onload = (evento) => {
      try {
        const matriz = bytesAMatriz(new Uint8Array(evento.target.result));
        resolve({ ...parsearArchivoCauciones(matriz, { fechaRueda }), archivoNombre: file.name });
      } catch (error) {
        console.error('caucionesImportService.leerArchivoCauciones failed:', { archivo: file?.name, error });
        reject(new Error(`No se pudo interpretar "${file?.name}": ${error.message}`));
      }
    };
    reader.readAsArrayBuffer(file);
  });

// ---------------------------------------------------------------------------
// Persistencia
// ---------------------------------------------------------------------------

/** Los ids de documento no admiten "/" ni "." sueltos. */
const sanitizarId = (valor) =>
  String(valor ?? '')
    .trim()
    .replace(/[/\\.#[\]$]/g, '_')
    .slice(0, 400);

export const idDeImportada = (partida) => sanitizarId(`${partida.mercado}_${partida.idExterno}`);

/** Ejecuta una lista de escrituras partiéndola en batches. */
const commitEnBatches = async (escrituras) => {
  for (let i = 0; i < escrituras.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    escrituras.slice(i, i + BATCH_SIZE).forEach((escritura) => {
      if (escritura.tipo === 'set') batch.set(escritura.ref, escritura.data, escritura.opciones || {});
      else if (escritura.tipo === 'update') batch.update(escritura.ref, escritura.data);
      else if (escritura.tipo === 'delete') batch.delete(escritura.ref);
    });
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
};

/** Trae las partidas ya importadas hoy. La colección se vacía en el cierre diario. */
export const fetchImportadas = async () => {
  const snapshot = await getDocs(collection(db, IMPORTADAS));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const docImportada = (partida, { caucionId, estadoConciliacion, userName, userId, archivoNombre }) => {
  const datos = {
    mercado: partida.mercado,
    idExterno: String(partida.idExterno),
    ordenExterna: String(partida.ordenExterna || ''),
    nroComitente: partida.nroComitente || '',
    cuit: partida.cuit || '',
    operacion: partida.operacion,
    moneda: partida.moneda,
    capital: numeroDe(partida.capital),
    tasa: numeroDe(partida.tasa),
    dias: numeroDe(partida.dias),
    montoVencimiento: numeroDe(partida.montoVencimiento),
    caucionId: caucionId || '',
    estadoConciliacion,
    importadoPor: userName || '',
    importadoPorId: userId || '',
    importadoAt: serverTimestamp(),
    archivoNombre: archivoNombre || '',
  };
  // Se omite en vez de mandar null: las reglas exigen timestamp si el campo está.
  if (partida.fechaConcertacion) {
    datos.fechaConcertacion = Timestamp.fromDate(partida.fechaConcertacion);
  }
  return datos;
};

/**
 * Guarda el resultado de una conciliación.
 *
 * Escribe en dos lugares, y cada uno tiene su motivo:
 *
 * - `cauciones/{id}/parciales/{mercado}_{idExterno}` con id determinístico: es
 *   lo que hace que reimportar el mismo archivo no duplique nada, y lo que
 *   mantiene funcionando el modal de parciales y el recálculo de
 *   `montoOperado` que hace la Cloud Function.
 * - `caucionesImportadas/{mercado}_{idExterno}`: es el único lugar donde pueden
 *   vivir las partidas que no matchearon con ninguna caución, y lo que permite
 *   que el panel de conciliación sobreviva a un refresh.
 *
 * Si una partida se reasigna a otra caución, se borra el parcial que había
 * quedado colgado de la anterior (la Cloud Function le recalcula el monto).
 *
 * @returns {{ partidasGuardadas: number, caucionesActualizadas: number, parcialesReasignados: number }}
 */
export const guardarImportacion = async ({
  conciliacion,
  archivoNombre,
  userName,
  userId,
}) => {
  try {
    const previas = await fetchImportadas();
    const caucionPrevia = new Map(previas.map((p) => [p.id, p.caucionId || '']));

    const escrituras = [];
    let parcialesReasignados = 0;

    const registrar = (partida, { caucionId, estadoConciliacion, conParcial = Boolean(caucionId) }) => {
      const idDoc = idDeImportada(partida);
      const anterior = caucionPrevia.get(idDoc);

      // La partida cambió de caución: hay que sacar el parcial de la anterior.
      if (anterior && anterior !== caucionId) {
        escrituras.push({
          tipo: 'delete',
          ref: doc(db, CAUCIONES, anterior, PARCIALES, idDoc),
        });
        parcialesReasignados += 1;
      }

      escrituras.push({
        tipo: 'set',
        ref: doc(db, IMPORTADAS, idDoc),
        data: docImportada(partida, { caucionId, estadoConciliacion, userName, userId, archivoNombre }),
      });

      if (conParcial) {
        escrituras.push({
          tipo: 'set',
          ref: doc(db, CAUCIONES, caucionId, PARCIALES, idDoc),
          // isValidParcial sólo admite estos cuatro campos: la clave externa va
          // en el id del documento, así que no hace falta tocar esa regla.
          data: {
            monto: numeroDe(partida.capital),
            tasa: numeroDe(partida.tasa),
            createdBy: userName || '',
            createdAt: serverTimestamp(),
          },
        });
      }
    };

    conciliacion.porCaucion.forEach((grupo) => {
      grupo.partidas.forEach((partida) =>
        registrar(partida, { caucionId: grupo.caucion.id, estadoConciliacion: CONCILIACION_ASIGNADA })
      );
      escrituras.push({
        tipo: 'update',
        ref: doc(db, CAUCIONES, grupo.caucion.id),
        data: updateDeCaucion(grupo, { userName }),
      });
    });

    conciliacion.sinImputar.forEach((grupo) => {
      grupo.partidas.forEach((partida) =>
        registrar(partida, {
          caucionId: '',
          estadoConciliacion: partida.ignoradaAMano ? CONCILIACION_IGNORADA : grupo.motivo,
        })
      );
    });

    // Sin parcial ni update: la caución cerrada a mano ya tiene su monto
    // operado, y si se cerró con parciales manuales sumarle este lo duplicaría.
    const yaCargadas = conciliacion.yaCargadas || [];
    yaCargadas.forEach((partida) =>
      registrar(partida, {
        caucionId: partida.caucionId,
        estadoConciliacion: CONCILIACION_YA_CARGADA,
        conParcial: false,
      })
    );

    await commitEnBatches(escrituras);

    const partidasGuardadas =
      conciliacion.porCaucion.reduce((acc, g) => acc + g.partidas.length, 0) +
      conciliacion.sinImputar.reduce((acc, g) => acc + g.partidas.length, 0) +
      yaCargadas.length;

    return {
      partidasGuardadas,
      caucionesActualizadas: conciliacion.porCaucion.length,
      parcialesReasignados,
    };
  } catch (error) {
    console.error('caucionesImportService.guardarImportacion failed:', { archivoNombre, error });
    throw error;
  }
};

/**
 * Crea una caución ya operada a partir de un grupo de partidas que no tenía
 * pedido previo, y le cuelga sus parciales.
 *
 * @param {object} grupo - elemento de `sinImputar`
 * @param {{ nroComitente: string, cliente: string, userName: string, userId: string, archivoNombre?: string }} datos
 */
export const crearCaucionDesdeSinImputar = async (grupo, { nroComitente, cliente, userName, userId, archivoNombre }) => {
  try {
    const comitente = nroComitente || grupo.nroComitente;
    if (!comitente) {
      throw new Error('Falta el número de comitente para crear la caución.');
    }

    // Crear es la única escritura de la importación que no es idempotente: el
    // resto usa ids determinísticos, pero acá cada llamada genera una caución
    // nueva. La cartera propia es la que más lo sufre: sus partidas no traen
    // CUIT, así que nunca resuelven comitente, nunca matchean un pedido y caen
    // siempre en "sin imputar" — y como la caución que se crea queda en estado
    // "Hecha", tampoco es candidata en la próxima conciliación. Reimportar el
    // archivo de la misma rueda la volvía a ofrecer como nueva y la duplicaba.
    const previas = await fetchImportadas();
    const caucionPrevia = new Map(previas.map((p) => [p.id, p.caucionId || '']));
    const yaImputadas = grupo.partidas.filter((partida) => caucionPrevia.get(idDeImportada(partida)));
    if (yaImputadas.length > 0) {
      const cauciones = new Set(yaImputadas.map((partida) => caucionPrevia.get(idDeImportada(partida))));
      throw new Error(
        `${yaImputadas.length} de estas ${grupo.partidas.length} partida(s) ya se importaron y están ` +
          `imputadas a ${cauciones.size === 1 ? 'una caución ya creada' : `${cauciones.size} cauciones ya creadas`}. ` +
          'No se creó una nueva para no duplicar la operación: refrescá la planilla para verla.'
      );
    }

    const caucionRef = doc(collection(db, CAUCIONES));
    const escrituras = [
      {
        tipo: 'set',
        ref: caucionRef,
        data: {
          operacion: grupo.operacion,
          nroComitente: comitente,
          cliente: cliente || '',
          dias: numeroDe(grupo.dias),
          monto: grupo.capitalTotal,
          tasa: grupo.tasaPonderada,
          moneda: grupo.moneda,
          mercado: grupo.mercado,
          comercial: '',
          comentarioComercial: `Creada desde la importación de ${grupo.mercado}${archivoNombre ? ` (${archivoNombre})` : ''}: no había pedido cargado.`,
          createdAt: serverTimestamp(),
          creadoPorNombre: userName || '',
          creadoPorId: userId || '',
          estado: 'Hecha',
          comentarioTrader: '',
          montoOperado: grupo.capitalTotal,
          tasaOperada: grupo.tasaPonderada,
          operador: userName || '',
        },
      },
    ];

    grupo.partidas.forEach((partida) => {
      const idDoc = idDeImportada(partida);
      escrituras.push({
        tipo: 'set',
        ref: doc(db, CAUCIONES, caucionRef.id, PARCIALES, idDoc),
        data: {
          monto: numeroDe(partida.capital),
          tasa: numeroDe(partida.tasa),
          createdBy: userName || '',
          createdAt: serverTimestamp(),
        },
      });
      escrituras.push({
        tipo: 'set',
        ref: doc(db, IMPORTADAS, idDoc),
        data: docImportada(partida, {
          caucionId: caucionRef.id,
          estadoConciliacion: CONCILIACION_ASIGNADA,
          userName,
          userId,
          archivoNombre,
        }),
      });
    });

    await commitEnBatches(escrituras);
    return caucionRef.id;
  } catch (error) {
    console.error('caucionesImportService.crearCaucionDesdeSinImputar failed:', { grupo: grupo?.clave, error });
    throw error;
  }
};
